// UserJobAlertsView Component - Tabs view updated for deployment
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Divider,
  Stack,
  Avatar,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Work as WorkIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../../components/LanguageSwitcher';

// API Base URL helper
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Qualification mapping
const QUALIFICATION_LABELS = {
  ssc: 'SSC / 10th',
  inter: 'Inter / 12th',
  degree: 'Degree',
  btech: 'B.Tech',
  iti: 'ITI / Diploma',
  nursing: 'Nursing',
  teacher: 'Teacher',
  ttc: 'TTC / B.Ed',
  pg: 'PG / Post Graduate',
  hallTicket: 'Hall Tickets',
  results: 'Results',
  admissionInfo: 'Admissions',
  counsellingInfo: 'Web Counselling Info',
};

// Category options - Qualification Categories
const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'ssc', label: 'SSC / 10th' },
  { value: 'inter', label: 'Inter / 12th' },
  { value: 'degree', label: 'Degree' },
  { value: 'btech', label: 'B.Tech' },
  { value: 'iti', label: 'ITI / Diploma' },
  { value: 'nursing', label: 'Nursing' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'ttc', label: 'TTC / B.Ed' },
  { value: 'pg', label: 'PG / Post Graduate' },
  { value: 'hallTicket', label: 'Hall Tickets' },
  { value: 'results', label: 'Results' },
  { value: 'admissionInfo', label: 'Admissions' },
  { value: 'counsellingInfo', label: 'Web Counselling Info' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'lastDate', label: 'Last Date (Ascending)' },
  { value: 'lastDateDesc', label: 'Last Date (Descending)' },
  { value: 'createdAt', label: 'Newest First' },
  { value: 'createdAtDesc', label: 'Oldest First' },
  { value: 'notificationName', label: 'Name (A-Z)' },
];

const UserJobAlertsView = () => {
  const { t, translateJobContent } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [jobAlerts, setJobAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('lastDate');
  const [selectedJob, setSelectedJob] = useState(null);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  useEffect(() => {
    document.title = 'Job Alerts - LOCSY User Dashboard';
    return () => {
      document.title = 'LOCSY Admin Dashboard';
    };
  }, []);

  // Fetch job alerts
  const fetchJobAlerts = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        isActive: 'true',
        limit: 1000,
      };
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await axios.get(`${API_BASE_URL}/jobalerts/public`, { params });
      if (response.data.success) {
        setJobAlerts(response.data.data || []);
      } else {
        setError('Failed to load job alerts');
      }
    } catch (err) {
      console.error('Error fetching job alerts:', err);
      setError('Failed to load job alerts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/jobalerts/stats/summary`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchJobAlerts();
    fetchStats();
  }, [categoryFilter, searchQuery]);

  // Filter and sort job alerts
  const filteredAndSortedAlerts = useMemo(() => {
    let filtered = [...jobAlerts];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.notificationName?.toLowerCase().includes(query) ||
          job.organization?.toLowerCase().includes(query) ||
          job.description?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'lastDate':
          if (!a.lastDate && !b.lastDate) return 0;
          if (!a.lastDate) return 1;
          if (!b.lastDate) return -1;
          return new Date(a.lastDate) - new Date(b.lastDate);
        case 'lastDateDesc':
          if (!a.lastDate && !b.lastDate) return 0;
          if (!a.lastDate) return 1;
          if (!b.lastDate) return -1;
          return new Date(b.lastDate) - new Date(a.lastDate);
        case 'createdAt':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'createdAtDesc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'notificationName':
          return (a.notificationName || '').localeCompare(b.notificationName || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [jobAlerts, searchQuery, sortBy]);

  // Handle job card click
  const handleJobClick = (job) => {
    const index = filteredAndSortedAlerts.findIndex((j) => j.id === job.id);
    setCurrentJobIndex(index >= 0 ? index : 0);
    setSelectedJob(job);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedJob(null);
    setCurrentJobIndex(0);
  };

  // Navigate to next/previous job
  const navigateJob = (direction) => {
    if (direction === 'next' && currentJobIndex < filteredAndSortedAlerts.length - 1) {
      const nextIndex = currentJobIndex + 1;
      setCurrentJobIndex(nextIndex);
      setSelectedJob(filteredAndSortedAlerts[nextIndex]);
    } else if (direction === 'prev' && currentJobIndex > 0) {
      const prevIndex = currentJobIndex - 1;
      setCurrentJobIndex(prevIndex);
      setSelectedJob(filteredAndSortedAlerts[prevIndex]);
    }
  };

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      navigateJob('next');
    } else if (isRightSwipe) {
      navigateJob('prev');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get days remaining
  const getDaysRemaining = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get qualification stats
  const qualificationStats = useMemo(() => {
    if (!stats?.byQualification) return [];
    return Object.entries(stats.byQualification)
      .map(([key, count]) => ({
        qualification: key,
        label: QUALIFICATION_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1),
        count,
      }))
      .filter((stat) => stat.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 1, sm: 2, md: 3 },
        px: { xs: 0, sm: 0 },
      }}
    >
      <Container 
        maxWidth="xl"
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        {/* Header with Language Switcher */}
        <Box 
          sx={{ 
            mb: { xs: 1.5, sm: 2, md: 3 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 1,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}
          >
            <Box component="span" sx={{ color: 'white' }}>KNLS</Box>
            <Box component="span" sx={{ mx: 0.5, color: 'rgba(255,255,255,0.8)' }}>-</Box>
            <Box component="span">{t('jobAlerts')}</Box>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <LanguageSwitcher useSettingsIcon={true} />
          </Box>
        </Box>

        {/* Statistics Section */}
        {stats && (
          <Paper
            elevation={3}
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              mb: { xs: 1.5, sm: 2, md: 3 },
              background: 'white',
              borderRadius: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: { xs: 1.5, sm: 2 }, 
                fontWeight: 'bold',
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TrendingUpIcon sx={{ mr: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
              {t('currentStatistics')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 1.5 },
                maxHeight: { xs: '180px', sm: '200px' },
                overflow: 'hidden',
                transition: 'max-height 0.3s ease',
              }}
            >
              {/* Active Jobs Card */}
              <Box
                sx={{
                  flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 calc(25% - 12px)', md: '0 1 calc(20% - 12px)' },
                  minWidth: { xs: 'calc(50% - 4px)', sm: 'calc(25% - 12px)', md: 'calc(20% - 12px)' },
                  textAlign: 'center',
                  p: { xs: 1.5, sm: 1.75, md: 2 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  minHeight: { xs: 80, sm: 85, md: 90 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  {stats.active || 0}
                </Typography>
                <Typography 
                  variant="caption"
                  sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                    mt: 0.5,
                  }}
                >
                  Active Jobs
                </Typography>
              </Box>
              {/* Qualification Stats Cards */}
              {qualificationStats.map((stat, index) => (
                <Box
                  key={stat.qualification}
                  sx={{
                    flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 calc(25% - 12px)', md: '0 1 calc(20% - 12px)' },
                    minWidth: { xs: 'calc(50% - 4px)', sm: 'calc(25% - 12px)', md: 'calc(20% - 12px)' },
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 1.75, md: 2 },
                    borderRadius: { xs: 2, sm: 2.5 },
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    minHeight: { xs: 80, sm: 85, md: 90 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  onClick={() => setCategoryFilter(stat.qualification)}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    {stat.count}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                      mt: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {t(stat.qualification) || stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Category Tabs - Single Line */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 0.5, sm: 1 },
            mb: { xs: 1.5, sm: 2, md: 3 },
            background: 'white',
            borderRadius: { xs: 2, sm: 2.5 },
          }}
        >
          {/* Purpose Description */}
          <Typography
            variant="caption"
            sx={{
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.5, sm: 0.75 },
              color: 'text.secondary',
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
              fontStyle: 'italic',
              display: 'block',
            }}
          >
            Search for :
          </Typography>
          <Tabs
            value={categoryFilter}
            onChange={(_, value) => setCategoryFilter(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 40, sm: 48 },
              '& .MuiTab-root': {
                minHeight: { xs: 40, sm: 48 },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                px: { xs: 1, sm: 1.5, md: 2 },
                py: { xs: 0.75, sm: 1 },
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700,
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              },
            }}
          >
            {CATEGORIES.map((cat) => (
              <Tab
                key={cat.value}
                value={cat.value}
                label={cat.value === 'all' ? 'All' : (t(cat.value) || cat.label)}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Search and Sort */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1.5, sm: 2, md: 2.5 },
            mb: { xs: 1.5, sm: 2, md: 3 },
            background: 'white',
            borderRadius: { xs: 2, sm: 2.5 },
          }}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder={t('searchJobs')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <FilterListIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>{t('sortBy')}</InputLabel>
                <Select
                  value={sortBy}
                  label={t('sortBy')}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                      {t(option.value) || option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Job Alerts Grid */}
        {!loading && !error && (
          <>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: { xs: 1, sm: 1.5 },
                color: 'white',
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                px: { xs: 1, sm: 0 },
                fontWeight: 600,
              }}
            >
              {filteredAndSortedAlerts.length} {filteredAndSortedAlerts.length !== 1 ? t('jobsFound') : t('jobFound')}
            </Typography>
            {filteredAndSortedAlerts.length === 0 ? (
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  textAlign: 'center',
                  background: 'white',
                  borderRadius: { xs: 2, sm: 2.5, md: 3 },
                }}
              >
                <InfoIcon sx={{ fontSize: { xs: 48, sm: 54, md: 60 }, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                  {t('noJobAlertsFound')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {t('tryAdjustingFilters')}
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                  {filteredAndSortedAlerts.slice(0, showAllNotifications ? filteredAndSortedAlerts.length : 1).map((job) => {
                    const daysRemaining = getDaysRemaining(job.lastDate);
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={job.id}>
                        <Card
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6,
                            },
                          }}
                          onClick={() => handleJobClick(job)}
                        >
                          <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Chip
                                label={t(job.category) || QUALIFICATION_LABELS[job.category] || job.category}
                                size="small"
                                color="primary"
                                sx={{ fontWeight: 'bold' }}
                              />
                              {job.isUrgent && (
                                <Chip
                                  label={t('urgent')}
                                  size="small"
                                  color="error"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="h6"
                              component="h3"
                              sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                              }}
                            >
                              {translateJobContent(job.notificationName)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ 
                                mb: 1,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              }}
                            >
                              {translateJobContent(job.organization)}
                            </Typography>
                            {job.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {translateJobContent(job.location)}
                                </Typography>
                              </Box>
                            )}
                            {job.lastDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {t('lastDate')}: {formatDate(job.lastDate)}
                                </Typography>
                              </Box>
                            )}
                            {daysRemaining !== null && daysRemaining >= 0 && (
                              <Chip
                                label={`${daysRemaining} ${daysRemaining !== 1 ? t('daysLeft') : t('dayLeft')}`}
                                size="small"
                                color={daysRemaining <= 7 ? 'error' : daysRemaining <= 30 ? 'warning' : 'success'}
                                sx={{ mt: 1 }}
                              />
                            )}
                          </CardContent>
                          <CardActions>
                            <Button size="small" fullWidth>
                              {t('viewDetails')}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                {/* Scroll Indicator for More Cards */}
                {!showAllNotifications && filteredAndSortedAlerts.length > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 3,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => setShowAllNotifications(true)}
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        boxShadow: 4,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {t('viewMoreJobs') || `View ${filteredAndSortedAlerts.length - 1} More Job${filteredAndSortedAlerts.length - 1 !== 1 ? 's' : ''}`}
                    </Button>
                  </Box>
                )}
                {/* Show All Remaining Cards */}
                {showAllNotifications && filteredAndSortedAlerts.length > 1 && (
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mt: 1 }}>
                    {filteredAndSortedAlerts.slice(1).map((job) => {
                      const daysRemaining = getDaysRemaining(job.lastDate);
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={job.id}>
                          <Card
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              cursor: 'pointer',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 6,
                              },
                            }}
                            onClick={() => handleJobClick(job)}
                          >
                            <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Chip
                                  label={t(job.category) || QUALIFICATION_LABELS[job.category] || job.category}
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {job.isUrgent && (
                                  <Chip
                                    label={t('urgent')}
                                    size="small"
                                    color="error"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                  fontWeight: 'bold',
                                  mb: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                                }}
                              >
                                {translateJobContent(job.notificationName)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ 
                                  mb: 1,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                }}
                              >
                                {translateJobContent(job.organization)}
                              </Typography>
                              {job.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {translateJobContent(job.location)}
                                  </Typography>
                                </Box>
                              )}
                              {job.lastDate && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {t('lastDate')}: {formatDate(job.lastDate)}
                                  </Typography>
                                </Box>
                              )}
                              {daysRemaining !== null && daysRemaining >= 0 && (
                                <Chip
                                  label={`${daysRemaining} ${daysRemaining !== 1 ? t('daysLeft') : t('dayLeft')}`}
                                  size="small"
                                  color={daysRemaining <= 7 ? 'error' : daysRemaining <= 30 ? 'warning' : 'success'}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </CardContent>
                            <CardActions>
                              <Button size="small" fullWidth>
                                {t('viewDetails')}
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {/* Job Detail Modal (Story-like View) */}
        <Dialog
          open={!!selectedJob}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              m: isMobile ? 0 : 2,
              borderRadius: isMobile ? 0 : 3,
              maxHeight: isMobile ? '100vh' : '90vh',
            },
          }}
        >
          {selectedJob && (
            <Box
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              sx={{ position: 'relative' }}
            >
              {/* Progress Bar */}
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={((currentJobIndex + 1) / filteredAndSortedAlerts.length) * 100}
                  sx={{
                    height: 4,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              </Box>

              {/* Language Switcher - Top Right */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 48,
                  zIndex: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 2,
                  p: 0.5,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <LanguageSwitcher variant="chips" />
              </Box>

              {/* Close Button */}
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 3,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Navigation Arrows */}
              {currentJobIndex > 0 && (
                <IconButton
                  onClick={() => navigateJob('prev')}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 3,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>
              )}
              {currentJobIndex < filteredAndSortedAlerts.length - 1 && (
                <IconButton
                  onClick={() => navigateJob('next')}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 3,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              )}

              {/* Job Counter */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 3,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption">
                  {currentJobIndex + 1} / {filteredAndSortedAlerts.length}
                </Typography>
              </Box>

              <DialogContent
                sx={{
                  p: 0,
                  overflowY: 'auto',
                  maxHeight: isMobile ? '100vh' : '90vh',
                }}
              >
                <Box sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      <Chip
                        label={t(selectedJob.category) || QUALIFICATION_LABELS[selectedJob.category] || selectedJob.category}
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      />
                      {selectedJob.isUrgent && (
                        <Chip label={t('urgent')} color="error" sx={{ fontWeight: 'bold' }} />
                      )}
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {translateJobContent(selectedJob.notificationName)}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      {translateJobContent(selectedJob.organization)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Key Information */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {selectedJob.location && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1">
                            <strong>{t('location')}:</strong> {translateJobContent(selectedJob.location)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {selectedJob.lastDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1">
                            <strong>{t('lastDate')}:</strong> {formatDate(selectedJob.lastDate)}
                            {(() => {
                              const days = getDaysRemaining(selectedJob.lastDate);
                              return days !== null && days >= 0
                                ? ` (${days} ${days !== 1 ? t('daysLeft') : t('dayLeft')})`
                                : '';
                            })()}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {selectedJob.numberOfVacancies && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1">
                            <strong>{t('vacancies')}:</strong> {selectedJob.numberOfVacancies}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {selectedJob.qualification && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1">
                            <strong>{t('qualification')}:</strong> {translateJobContent(selectedJob.qualification)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {selectedJob.ageLimit && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1">
                          <strong>{t('ageLimit')}:</strong> {translateJobContent(selectedJob.ageLimit)}
                        </Typography>
                      </Grid>
                    )}
                    {selectedJob.salary && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1">
                          <strong>{t('salary')}:</strong> {translateJobContent(selectedJob.salary)}
                        </Typography>
                      </Grid>
                    )}
                    {selectedJob.applicationFee && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1">
                          <strong>{t('applicationFee')}:</strong> {translateJobContent(selectedJob.applicationFee)}
                        </Typography>
                      </Grid>
                    )}
                    {selectedJob.examDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1">
                            <strong>{t('examDate') || 'Exam Date'}:</strong> {formatDate(selectedJob.examDate)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {selectedJob.resultInfo?.resultDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1">
                            <strong>{t('resultDate') || 'Result Date'}:</strong> {formatDate(selectedJob.resultInfo.resultDate)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Description */}
                  {selectedJob.description && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('description')}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                        {translateJobContent(selectedJob.description)}
                      </Typography>
                    </>
                  )}

                  {/* Eligibility */}
                  {selectedJob.eligibility && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('eligibility')}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                        {translateJobContent(selectedJob.eligibility)}
                      </Typography>
                    </>
                  )}

                  {/* Important Dates */}
                  {selectedJob.importantDates && selectedJob.importantDates.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('importantDates')}
                      </Typography>
                      <Stack spacing={1}>
                        {selectedJob.importantDates.map((date, idx) => (
                          <Typography key={idx} variant="body1">
                            • {translateJobContent(date)}
                          </Typography>
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* Required Documents */}
                  {selectedJob.requiredDocuments && selectedJob.requiredDocuments.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('requiredDocuments')}
                      </Typography>
                      <Stack spacing={1}>
                        {selectedJob.requiredDocuments.map((doc, idx) => (
                          <Typography key={idx} variant="body1">
                            • {translateJobContent(doc)}
                          </Typography>
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* Action Buttons */}
                  <Divider sx={{ my: 3 }} />
                  <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    {selectedJob.notificationUrl && (
                      <Button
                        variant="outlined"
                        color="primary"
                        href={selectedJob.notificationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        fullWidth
                      >
                        {t('viewNotification')}
                      </Button>
                    )}
                    {selectedJob.officialWebsite && (
                      <Button
                        variant="outlined"
                        href={selectedJob.officialWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        fullWidth
                      >
                        {t('officialWebsite')}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </DialogContent>
            </Box>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserJobAlertsView;
